import React, { useEffect, useState } from 'react'
import { Table, Button, Badge, Typography, Switch, Tooltip, Collapse, message, Empty } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import { useProject } from 'src/Hooks/MentorHooks/useProjectsHooks'
import ProjectTimeline from '../table'
import apiClient from 'src/Common/apiClient'
import { thumbUrl } from 'src/Common/fileAsset'
import { downloadFileAsset } from 'src/Common/downloadFile'
import 'src/Pages/LibraryPages/librarypages.scss'

const { Text } = Typography
const { Panel } = Collapse

const ProjectIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="7" width="18" height="14" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18" />
  </svg>
)

const BackIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
)

const ChatIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
)

const ProjectsDetail = () => {
  const token = localStorage.getItem('token')
  const { id } = useParams()
  const navigate = useNavigate()
  const { projectData, loading, updating, handleToggleResubmission } = useProject(id)
  const [certStatusData, setCertStatusData] = useState([])
  const [certActionBusyId, setCertActionBusyId] = useState(null)

  const handleGroupConversationClick = () => {
    if (projectData?.group?.hasGroupChat && projectData?.group?.conversationId) {
      navigate('/messaging', { state: { selectedChatId: projectData.group.conversationId } })
    }
  }

  const handleDownloadInternship = async (taskId) => {
    try {
      await downloadFileAsset({
        path: '/InternshipProject/Download-Task-Submission',
        params: { taskId },
        fallbackName: 'submission',
      })
    } catch (e) {
      message.error('Failed to download the file. Please try again.')
    }
  }

  useEffect(() => {
    const fetchCertStatus = async () => {
      try {
        const res = await apiClient.get(
          `/InternshipCertificate/Mentor/Project/certification-status?projectId=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        // api-dev2: tolerate a paginated ({ items }) list.
        const d = res.data
        setCertStatusData(Array.isArray(d) ? d : d?.items ?? d)
      } catch (err) {
        console.error('Error fetching certificate status:', err)
      }
    }

    if (id) {
      fetchCertStatus()
    }
  }, [id])

  const taskColumns = [
    { title: 'Task', dataIndex: 'title', key: 'title' },
    {
      title: 'Assigned To',
      dataIndex: 'groupMemberId',
      key: 'groupMemberId',
      render: (id) => {
        const member = projectData?.group?.members.find((m) => m.memberId === id)
        return member ? member.studentName : 'Unknown'
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let badgeStatus, text

        switch (status) {
          case 0:
            badgeStatus = 'error'
            text = 'Pending'
            break
          case 1:
            badgeStatus = 'processing'
            text = 'In Progress'
            break
          case 2:
            badgeStatus = 'success'
            text = 'Submitted'
            break
          default:
            badgeStatus = 'default'
            text = 'Unknown'
        }

        return <Badge status={badgeStatus} text={text} />
      },
    },
    {
      title: 'Deadline',
      dataIndex: 'deadline',
      key: 'deadline',
      render: (text) => (text ? moment(text).format('DD-MM-YYYY') : 'N/A'),
    },
    {
      title: 'Submitted File',
      key: 'submittedFile',
      render: (_, record) => {
        if (!record.submittedFileName) {
          return 'No file submitted'
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {thumbUrl(record.submittedFileThumbnailUrl) && (
              <img
                src={thumbUrl(record.submittedFileThumbnailUrl)}
                alt={record.submittedFileName}
                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
              />
            )}
            <span>{record.submittedFileName}</span>
            <Button type="link" onClick={() => handleDownloadInternship(record.taskId)}>
              View Submitted File
            </Button>
          </div>
        )
      },
    },
    {
      title: 'Allow Resubmission',
      key: 'enableSubmission',
      render: (_, record) => {
        const isDisabled = !record.submittedFileName
        return (
          <Tooltip title={isDisabled ? 'Task is not submitted yet!' : ''}>
            <Switch
              checked={record.enableSubmission}
              onChange={() => handleToggleResubmission(record.taskId, record.enableSubmission)}
              disabled={updating || isDisabled}
            />{' '}
          </Tooltip>
        )
      },
    },
  ]

  const columns = [
    {
      title: 'Group Member Name',
      dataIndex: 'studentName',
      key: 'studentName',
    },
    {
      title: 'Progress Report',
      key: 'progressReport',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() =>
            navigate(`/status-report/${record.memberId}`, {
              state: { studentName: record.studentName },
            })
          }
        >
          View Reports{' '}
          {record.statusReportAlertCount > 0 && (
            <Typography.Text type="danger">({record.statusReportAlertCount})</Typography.Text>
          )}
        </Button>
      ),
    },

    {
      title: 'Issues & Challenges',
      key: 'issuesChallenges',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() =>
            navigate(`/issues-and-report/${record.memberId}`, {
              state: { studentName: record.studentName },
            })
          }
        >
          Issues & Challenges{' '}
          {record.issueAlertCount > 0 && (
            <Typography.Text type="danger">({record.issueAlertCount})</Typography.Text>
          )}
        </Button>
      ),
    },
    {
      title: 'Certificate Status',
      key: 'certificateStatus',
      render: (_, record) => {
        const certInfo = certStatusData.find((item) => item.memberId === record.memberId)

        const isCertActionBusy = certActionBusyId === certInfo?.memberId

        const handleIssueCertificate = async () => {
          if (certActionBusyId) return
          try {
            setCertActionBusyId(certInfo.memberId)
            await apiClient.post(
              `/InternshipCertificate/Mentor/IssueCertificate?projectId=${certInfo.projectId}&groupMemberId=${certInfo.memberId}`,
              {}, // empty body
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            )

            // Refresh the certificate status
            const res = await apiClient.get(
              `/InternshipCertificate/Mentor/Project/certification-status?projectId=${id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            )
            setCertStatusData(Array.isArray(res.data) ? res.data : res.data?.items ?? res.data)
          } catch (error) {
            console.error('Error issuing certificate:', error.response?.data || error.message)
          } finally {
            setCertActionBusyId(null)
          }
        }

        const handleFailedCertificate = async () => {
          if (certActionBusyId) return
          try {
            setCertActionBusyId(certInfo.memberId)
            await apiClient.post(
              `/InternshipCertificate/Mentor/Mark-as-Failed?projectId=${certInfo.projectId}&groupMemberId=${certInfo.memberId}`,
              {}, // empty body
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            )

            // Refresh the certificate status
            const res = await apiClient.get(
              `/InternshipCertificate/Mentor/Project/certification-status?projectId=${id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            )
            setCertStatusData(Array.isArray(res.data) ? res.data : res.data?.items ?? res.data)
          } catch (error) {
            console.error('Error issuing certificate:', error.response?.data || error.message)
          } finally {
            setCertActionBusyId(null)
          }
        }

        if (!certInfo) return <Text type="secondary">No status</Text>

        if (certInfo.action === 1) {
          return <Text type="secondary">{certInfo.alertText}</Text>
        } else if (certInfo.action === 2) {
          return (
            <>
              <Button
                type="primary"
                size="small"
                onClick={handleIssueCertificate}
                loading={isCertActionBusy}
                disabled={isCertActionBusy}
              >
                Issue Certificate
              </Button>
              <br />
              <Text type="success">{certInfo.alertText}</Text>
            </>
          )
        } else if (certInfo.action === 3) {
          return (
            <>
              <Button
                type="primary"
                size="small"
                onClick={handleFailedCertificate}
                loading={isCertActionBusy}
                disabled={isCertActionBusy}
              >
                Not Qualified
              </Button>
              <br />
              <Text type="danger">{certInfo.alertText}</Text>
            </>
          )
        } else {
          return <Text type="secondary">Unknown action</Text>
        }
      },
    },
  ]

  return (
    <div className="lpg">
      <style>{`
        .vpd-sect { margin-bottom: 18px; }
        .vpd-sect:last-child { margin-bottom: 0; }
        .vpd-overview__title { font-family:'Bricolage Grotesque',sans-serif; margin:0 0 16px;
          color:#16233f; font-size:1.2rem; font-weight:800; letter-spacing:-0.01em; line-height:1.2; }
        .vpd-sect__title { display:flex; align-items:center; gap:9px; margin:0 0 16px;
          color:#16233f; font-size:1.02rem; font-weight:800; letter-spacing:-0.01em; }
        .vpd-sect__title::before { content:''; flex:none; width:4px; height:18px; border-radius:999px;
          background:linear-gradient(180deg,#3b82f6,#2563eb); }
        .vpd-kv { display:grid; grid-template-columns:max-content 1fr; gap:12px 20px; margin-bottom:18px; }
        .vpd-kv__k { color:#6b7794; font-size:0.75rem; font-weight:700; text-transform:uppercase;
          letter-spacing:0.05em; padding-top:2px; }
        .vpd-kv__v { color:#16233f; font-size:0.92rem; line-height:1.55; }
        @media (max-width:560px){
          .vpd-kv { grid-template-columns:1fr; gap:4px 0; }
          .vpd-kv__k { margin-top:10px; }
          .vpd-kv__k:first-child { margin-top:0; }
        }
      `}</style>

      <div className="lpg__head">
        <div className="lpg__head-main">
          <span className="lpg__head-ic">
            <ProjectIcon />
          </span>
          <div>
            <h1 className="lpg__title">Project Detail</h1>
            <p className="lpg__sub">Project details and phases.</p>
          </div>
        </div>
        <button type="button" className="lpg__back" onClick={() => navigate('/projects')}>
          <BackIcon /> Back
        </button>
      </div>

      {loading ? (
        <div className="lpg__state">
          <div className="lpg__spin" />
          Loading...
        </div>
      ) : projectData ? (
        <>
          <div className="lpg__panel-box vpd-sect">
            <h2 className="vpd-overview__title">{projectData.title || 'N/A'}</h2>

            <div className="vpd-kv">
              <span className="vpd-kv__k">Description</span>
              <span className="vpd-kv__v">{projectData.description || 'N/A'}</span>

              <span className="vpd-kv__k">Assigned Group</span>
              <span className="vpd-kv__v">{projectData.groupName || 'N/A'}</span>

              <span className="vpd-kv__k">Timeline</span>
              <span className="vpd-kv__v">
                {projectData.startDate
                  ? new Date(projectData.startDate).toLocaleDateString()
                  : 'N/A'}{' '}
                to{' '}
                {projectData.endDate ? new Date(projectData.endDate).toLocaleDateString() : 'N/A'}
              </span>
            </div>

            <button
              type="button"
              className="lpg__btn-primary"
              onClick={handleGroupConversationClick}
            >
              <ChatIcon />
              Group Conversation
            </button>
          </div>

          <div className="lpg__panel-box vpd-sect">
            <h3 className="vpd-sect__title">Group Members</h3>
            <Table
              bordered
              dataSource={projectData.group?.members || []}
              columns={columns}
              rowKey="memberId"
              pagination={true}
            />
          </div>

          <div className="lpg__panel-box vpd-sect">
            <h3 className="vpd-sect__title">Project Timeline</h3>
            <ProjectTimeline />
          </div>

          <div className="lpg__panel-box vpd-sect">
            <h3 className="vpd-sect__title">Weekly Plans</h3>
            <Collapse accordion>
              {projectData.weeklyPlans?.map((week, index) => (
                <Panel
                  header={
                    <>
                      Week {week.weekNumber} (<Text strong> Goal:</Text> {week.goal} )
                    </>
                  }
                  key={index}
                >
                  <p>
                    <strong>Tasks Summary:</strong>
                  </p>
                  <ul>
                    {week.tasksSummary?.map((task, taskIndex) => (
                      <li key={taskIndex}>{task}</li>
                    ))}
                  </ul>
                </Panel>
              ))}
            </Collapse>
          </div>

          <div className="lpg__panel-box vpd-sect">
            <h3 className="vpd-sect__title">Task Management</h3>
            <Table
              columns={taskColumns}
              dataSource={projectData.tasks || []}
              pagination={true}
              bordered
            />
          </div>
        </>
      ) : (
        <div className="lpg__panel-box">
          <Empty description="Failed to load project" />
        </div>
      )}
    </div>
  )
}

export default ProjectsDetail
